import './Form.scss'
import { useForm } from 'react-hook-form'
import { Field, Input, Select, Switch } from '@fluentui/react-components'
import { Button } from '../Button'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { useGetSearchParam } from '@/hooks'

type MessagesProjectsOrganisationProject = RestSchemas['Messages.Projects.OrganisationProject']
type MessagesProjectsProject = RestSchemas['Messages.Projects.Project']

type OrganisationProjectFormProps = {
  onSubmit: () => void
  edit?: boolean
  selectedOrganisationProject?: MessagesProjectsOrganisationProject
}

type OrganisationProjectForm = MessagesProjectsOrganisationProject & {
  frequency: string
}

export const OrganisationProjectForm = (props: OrganisationProjectFormProps) => {
  const orgId = useGetSearchParam<number>('orgId')
  const queryClient = useQueryClient()
  const {
    putProjectsApiAddProject,
    postProjectsApiUpdateProject,
    getProjectsApiListOrganisationProjects,
    getSubscriptionsApiGetOrganisations,
  } = createRestClient()

  const invalidateQueries = () => {
    getProjectsApiListOrganisationProjects.invalidateQueries(
      {
        parameters: { query: { orgId } },
      },
      queryClient
    )
    getSubscriptionsApiGetOrganisations.invalidateQueries({}, queryClient)
  }

  const onMutationSuccess = () => {
    props.onSubmit()
    toast.success(props.edit ? 'Project successfully updated' : 'Project successfully added')
    reset()
    invalidateQueries()
  }

  const { mutate: addProject } = putProjectsApiAddProject.useMutation(undefined, {
    onSuccess: onMutationSuccess,
  })
  const { mutate: updateProject } = postProjectsApiUpdateProject.useMutation(undefined, {
    onSuccess: onMutationSuccess,
  })

  const {
    register,
    formState: { errors },
    // setError,
    reset,
    handleSubmit,
    // control
  } = useForm<OrganisationProjectForm>({
    defaultValues: props.selectedOrganisationProject ?? {
      projectId: undefined,
      name: '',
      isFrozen: false,
      workspaceCount: 0,
      frequency: 'Daily',
    },
  })

  const onSubmit = (data: OrganisationProjectForm) => {
    if (!orgId) {
      toast.error('No organisation ID found')
      return
    }
    const organisationProject: MessagesProjectsProject = {
      organisationId: orgId,
      id: data.projectId || undefined,
      name: data.name,
      isFrozen: data.isFrozen,
      frequency: data.frequency,
    }

    if (props.edit) {
      updateProject({
        body: organisationProject,
      })
    } else {
      addProject({
        body: organisationProject,
      })
    }
  }

  return (
    <div className="form-wrapper">
      <div>
        <Field label="Name" validationState={errors.name ? 'error' : 'none'} validationMessage={errors.name?.message}>
          <Input {...register('name', { required: 'Name is required', maxLength: 100 })} />
        </Field>

        <Field
          label="Frozen"
          validationState={errors.isFrozen ? 'error' : 'none'}
          validationMessage={errors.isFrozen?.type === 'maxLength' ? 'Max length exceeded' : undefined}
        >
          <Switch {...register('isFrozen')} />
        </Field>

        <Field
          label="Frequency"
          validationState={errors.frequency ? 'error' : 'none'}
          validationMessage={errors.frequency?.message}
        >
          <Select {...register('frequency')}>
            <option value={'Daily'}>Daily</option>
            <option value={'Weekly'}>Weekly</option>
            <option value={'Monthly'}>Monthly</option>
            <option value={'Annually'}>Annually</option>
            <option value={'Ad hoc'}>Ad hoc</option>
          </Select>
        </Field>

        {/* <Field
          label="Workspace count"
          validationState={errors.workspaceCount ? "error" : "none"}
          validationMessage={errors.workspaceCount?.message}
          >
          <Input type="number" {...register("workspaceCount")} />
        </Field> */}
      </div>
      <div className="form-action-container">
        <Button text="Submit" onClick={handleSubmit(onSubmit)} />
      </div>
    </div>
  )
}
